import React, { useEffect, useMemo, useState } from "react";
import { adminGet, adminPut } from "../../../Services";
import { toast } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import {
  ClientSideRowModelModule,
  GridStateModule,
  ModuleRegistry,
} from "ag-grid-community";
import { Button, Modal, Form, Spinner, Badge, Row, Col, Card } from "react-bootstrap";
import { formatDateTime } from "../../../utils/helpers";

ModuleRegistry.registerModules([GridStateModule, ClientSideRowModelModule]);

const getNextYearMarch31 = () => {
  const nextYear = new Date().getFullYear() + 1;
  return `${nextYear}-03-31`;
};

const getDesignationLabel = (member) => {
  const assignments = member?.DesignationAssignments || [];
  if (!assignments.length) return "N/A";
  const first = assignments[0];
  const scope = first?.Designation?.scope || first?.level || "";
  const name = first?.Designation?.name || "";
  return `${scope} ${name}`.trim() || "N/A";
};

const getImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `https://manavadhikarforum.in${url}`;
};
const CERTIFIER_URL =
  process.env.REACT_APP_CERTIFIER_URL || "https://certifier.duckdns.org/api/generate";
const CERTIFIER_API_KEY = process.env.REACT_APP_CERTIFIER_API_KEY || "";

const toDDMMYYYY = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const toDDDashMMDashYYYY = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const toDOJFormat = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("en-IN", { month: "long" });
  const yyyy = d.getFullYear();
  return `${dd}-${month}-${yyyy}`;
};

const getWorkingArea = (member) => {
  const first = member?.DesignationAssignments?.[0];
  const scope = first?.Designation?.scope || first?.level || "";
  if (scope === "National") return "India";
  if (scope === "State") return first?.state || "";
  if (scope === "District") return first?.district || first?.state || "";
  if (scope === "Block")
    return first?.block || first?.district || first?.state || "";
  return first?.block || first?.district || first?.state || "";
};

const validateIdCardFields = (name, position) => {
  const messages = [];
  if ((name || "").trim().length > 32) {
    messages.push("Name must be 32 characters or less.");
  }
  if ((position || "").trim().length > 32) {
    messages.push("Designation must be 32 characters or less.");
  }
  return messages;
};

const RenewalsPending = () => {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  });

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [renewalDate, setRenewalDate] = useState(getNextYearMarch31());
  const [adminRemarks, setAdminRemarks] = useState("");
  const [approving, setApproving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [renewedMember, setRenewedMember] = useState(null);
  const [renewedValidDate, setRenewedValidDate] = useState("");
  const [hasDownloadedDoc, setHasDownloadedDoc] = useState(false);
  const [isGeneratingDoc, setIsGeneratingDoc] = useState(false);
  const [showDocValidationModal, setShowDocValidationModal] = useState(false);
  const [docValidationMessages, setDocValidationMessages] = useState([]);
  const [customIdCardName, setCustomIdCardName] = useState("");
  const [customIdCardDesignation, setCustomIdCardDesignation] = useState("");
  const [needsNameFix, setNeedsNameFix] = useState(false);
  const [needsDesignationFix, setNeedsDesignationFix] = useState(false);
  const [showIdCardDetailsModal, setShowIdCardDetailsModal] = useState(false);
  const [idCardDetails, setIdCardDetails] = useState({
    name: "",
    position: "",
    phone: "",
    workingArea: "",
    validDate: "",
    district: "",
    statePin: "",
    vehicleNumber: "",
    idCardNumber: "",
    photoUrl: "",
  });
  const [idCardEditable, setIdCardEditable] = useState({
    name: false,
    position: false,
    phone: false,
    workingArea: false,
    validDate: false,
    district: false,
    statePin: false,
    vehicleNumber: false,
    idCardNumber: false,
    photoUrl: false,
  });

  const getRequestId = (r) => r?.id ?? r?._id;

  const fetchPending = () => {
    setLoading(true);
    const searchTerm = search.trim();
    const appliedSearch = searchTerm.length >= 3 ? searchTerm : "";
    const query = new URLSearchParams({
      page: pagination.page,
      limit: pagination.limit,
      ...(appliedSearch ? { search: appliedSearch } : {}),
    }).toString();

    adminGet(`/api/admin/renewals/pending?${query}`)
      .then((res) => {
        setRequests(res.data?.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data?.total || 0,
          page: res.data?.page || prev.page,
          limit: res.data?.limit || prev.limit,
          pages: res.data?.pages || 1,
        }));
      })
      .catch((err) => {
        const msg = err?.response?.data?.error || "Failed to load renewals";
        toast.error(msg);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPending();
  }, [pagination.page, pagination.limit, search]);

  const openApprove = (req) => {
    setSelectedRequest(req);
    setRenewalDate(getNextYearMarch31());
    setAdminRemarks("");
    setShowApproveModal(true);
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;

    const requestId = getRequestId(selectedRequest);
    if (!requestId) {
      toast.error("Renewal request id not found.");
      return;
    }

    const payload = {};
    if (renewalDate) payload.renewal_date = renewalDate;
    if (adminRemarks?.trim()) payload.admin_remarks = adminRemarks.trim();

    setApproving(true);
    adminPut(`/api/admin/renewals/approve/${requestId}`, payload)
      .then(() => {
        toast.success("Renewal approved successfully.");
        setRenewedMember(selectedRequest?.Member || null);
        setRenewedValidDate(renewalDate || getNextYearMarch31());
        setHasDownloadedDoc(false);
        setShowSuccessModal(true);
        setShowApproveModal(false);
        setSelectedRequest(null);
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.error || err?.response?.data?.message || "";
        toast.error(msg || "Failed to approve renewal.");
      })
      .finally(() => setApproving(false));
  };

  const downloadBlob = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const generateDocument = async (documentType, overrideData = null) => {
    if (!renewedMember) return;
    const defaultName = renewedMember?.name || "";
    const defaultPosition = getDesignationLabel(renewedMember).toUpperCase();
    const nameForCard = overrideData?.name ?? defaultName;
    const position = overrideData?.position ?? defaultPosition;
    if (documentType === "id_card") {
      const validationMessages = validateIdCardFields(nameForCard, position);
      if (validationMessages.length > 0) {
        setDocValidationMessages(validationMessages);
        setCustomIdCardName(nameForCard);
        setCustomIdCardDesignation(position);
        setNeedsNameFix((nameForCard || "").trim().length > 32);
        setNeedsDesignationFix((position || "").trim().length > 32);
        setShowDocValidationModal(true);
        return;
      }
    }
    setIsGeneratingDoc(true);
    try {
      const basePayload =
        documentType === "id_card"
          ? {
              document_type: "id_card",
              name: nameForCard,
              position,
              phone: renewedMember?.mobile_no || "",
              workingArea: getWorkingArea(renewedMember),
              validDate: toDDMMYYYY(renewedValidDate || renewedMember?.expiry_date),
              district: `Distt-${renewedMember?.district || ""}`,
              statePin: `State-${renewedMember?.state || ""} Pin Code -${renewedMember?.pin || ""}`,
              vehicleNumber: renewedMember?.vehicle_no || " ",
              idCardNumber: renewedMember?.registration_id || "",
              photoUrl: getImageUrl(renewedMember?.photo_url),
            }
          : {
              document_type: "certificate",
              name: renewedMember?.name || "",
              regIdNumber: renewedMember?.registration_id || "",
              state: renewedMember?.state || "",
              doj: toDOJFormat(renewedMember?.member_since),
              dob: toDDDashMMDashYYYY(renewedMember?.dob),
              photoUrl: getImageUrl(renewedMember?.photo_url),
            };
      const payload =
        documentType === "id_card" && overrideData
          ? {
              ...basePayload,
              ...overrideData,
              vehicleNumber:
                (overrideData.vehicleNumber ?? basePayload.vehicleNumber) || " ",
            }
          : basePayload;

      const res = await fetch(CERTIFIER_URL, {
        method: "POST",
        headers: {
          "x-api-key": CERTIFIER_API_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let msg = "Failed to generate document.";
        try {
          const error = await res.json();
          msg = error?.error || error?.message || msg;
        } catch (_) {}
        toast.error(msg);
        return;
      }

      const blob = await res.blob();
      const fileName =
        documentType === "id_card"
          ? `${renewedMember?.registration_id || "id_card"}.jpg`
          : `${renewedMember?.registration_id || "certificate"}_certificate.jpg`;
      downloadBlob(blob, fileName);
      setHasDownloadedDoc(true);
      toast.success(
        documentType === "id_card"
          ? "ID card downloaded successfully."
          : "Certificate downloaded successfully."
      );
    } catch (err) {
      toast.error(err?.message || "Failed to generate document.");
    } finally {
      setIsGeneratingDoc(false);
    }
  };

  const renderPaymentBadge = useMemo(() => {
    return (req) => {
      const state =
        req?.PaymentReceipt?.state ||
        req?.PaymentReceipt?.status ||
        req?.payment_status ||
        req?.transaction_status;
      const value = state || "Pending";

      const bg =
        value === "success" || value === "COMPLETED"
          ? "success"
          : value === "failed" || value === "error"
          ? "danger"
          : "warning";

      return <Badge bg={bg}>{value}</Badge>;
    };
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "View",
        width: 90,
        pinned: "left",
        cellRenderer: (p) => (
          <div
            className="text-primary"
            style={{ cursor: "pointer", textAlign: "center" }}
            onClick={() => openApprove(p.data)}
            title="View / Approve"
          >
            <i className="bi bi-eye" style={{ fontSize: "1.2rem" }}></i>
          </div>
        ),
      },
      {
        headerName: "Member",
        valueGetter: (p) => p.data?.Member?.name || "-",
      },
      {
        headerName: "Reg. ID",
        valueGetter: (p) => p.data?.Member?.registration_id || "-",
      },
      {
        headerName: "State",
        valueGetter: (p) => p.data?.Member?.state || "-",
      },
      {
        headerName: "District",
        valueGetter: (p) => p.data?.Member?.district || "-",
      },
      {
        headerName: "Block",
        valueGetter: (p) => p.data?.Member?.block || "-",
      },
      {
        headerName: "Payment",
        cellRenderer: (p) => renderPaymentBadge(p.data),
      },
      {
        headerName: "Requested On",
        valueGetter: (p) =>
          p.data?.createdAt || p.data?.created_at || p.data?.PaymentReceipt?.createdAt,
        valueFormatter: (p) => formatDateTime(p.value) || "-",
      },
    ],
    [approving, renderPaymentBadge]
  );

  return (
    <div className="p-2 pt-0">
      <div className="mb-2 d-flex align-items-center justify-content-between">
        <h3 className="fw-medium" style={{ color: "#1a5276" }}>
          Renewal Requests
        </h3>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={fetchPending}
          disabled={loading}
        >
          Refresh
        </Button>
      </div>

      <div className="mb-3 d-flex justify-content-between align-items-center">
        <Form.Control
          type="text"
          placeholder="Search name, id, state, district, block..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPagination((p) => ({ ...p, page: 1 }));
          }}
          style={{ width: "350px" }}
        />
        <Form.Select
          style={{ width: "100px" }}
          value={pagination.limit}
          onChange={(e) =>
            setPagination((p) => ({
              ...p,
              page: 1,
              limit: parseInt(e.target.value, 10),
            }))
          }
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </Form.Select>
      </div>

      <div className="position-relative">
        {loading && (
          <div className="d-flex justify-content-center align-items-center position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75">
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        <div className="ag-theme-alpine" style={{ height: "80vh", width: "100%" }}>
          <AgGridReact
            columnDefs={columnDefs}
            rowModelType="clientSide"
            rowData={requests}
            cacheBlockSize={pagination.limit}
            pagination={false}
            overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading...</span>'
            overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No records found.</span>'
          />
        </div>

        <div className="d-flex justify-content-between align-items-center">
          <Button
            variant="outline-secondary"
            disabled={pagination.page <= 1}
            onClick={() =>
              setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))
            }
          >
            Previous
          </Button>
          <span>
            Page {pagination.page} of {pagination.pages || 1}
          </span>
          <Button
            variant="outline-secondary"
            disabled={pagination.page >= (pagination.pages || 1)}
            onClick={() =>
              setPagination((p) => ({
                ...p,
                page: Math.min(p.pages || 1, p.page + 1),
              }))
            }
          >
            Next
          </Button>
        </div>
      </div>

      <Modal
        show={showApproveModal}
        onHide={() => setShowApproveModal(false)}
        centered
        size="lg"
        className="application-review-modal"
      >
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title>
            <i className="bi bi-clipboard-check me-2"></i>
            Approve Renewal
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <Row className="g-0">
            <Col md={8} className="p-4">
              <div className="section-title fs-5">
                <i className="bi bi-person-badge me-2"></i>
                Member Information
              </div>
              <Row>
                <Col sm={6}>
                  <div className="data-field">
                    <label>Full Name</label>
                    <p>{selectedRequest?.Member?.name || "-"}</p>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="data-field">
                    <label>Registration ID</label>
                    <p>{selectedRequest?.Member?.registration_id || "-"}</p>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col sm={6}>
                  <div className="data-field">
                    <label>Date of Birth</label>
                    <p>{selectedRequest?.Member?.dob || "-"}</p>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="data-field">
                    <label>Profession</label>
                    <p>{selectedRequest?.Member?.profession || "-"}</p>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col sm={6}>
                  <div className="data-field">
                    <label>Mobile Number</label>
                    <p>{selectedRequest?.Member?.mobile_no || "-"}</p>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="data-field">
                    <label>Email</label>
                    <p>{selectedRequest?.Member?.email || "-"}</p>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col sm={6}>
                  <div className="data-field">
                    <label>Designation</label>
                    <p>{getDesignationLabel(selectedRequest?.Member)}</p>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="data-field">
                    <label>Aadhaar</label>
                    <p>{selectedRequest?.Member?.aadhaar_no || "-"}</p>
                  </div>
                </Col>
              </Row>

              <div className="section-title mt-4 fs-5">
                <i className="bi bi-geo-alt me-2"></i>
                Address Details
              </div>
              <Row>
                <Col sm={4}>
                  <div className="data-field">
                    <label>State</label>
                    <p>{selectedRequest?.Member?.state || "-"}</p>
                  </div>
                </Col>
                <Col sm={4}>
                  <div className="data-field">
                    <label>District</label>
                    <p>{selectedRequest?.Member?.district || "-"}</p>
                  </div>
                </Col>
                <Col sm={4}>
                  <div className="data-field">
                    <label>Block</label>
                    <p>{selectedRequest?.Member?.block || "-"}</p>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col sm={6}>
                  <div className="data-field">
                    <label>PIN</label>
                    <p>{selectedRequest?.Member?.pin || "-"}</p>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="data-field">
                    <label>Current Validity</label>
                    <p>{selectedRequest?.Member?.expiry_date || "N/A"}</p>
                  </div>
                </Col>
              </Row>
            </Col>

            <Col md={4} className="documents-sidebar p-3">
              <div className="section-title fs-6">
                <i className="bi bi-receipt me-2"></i>
                Renewal Payment
              </div>
              <div className="data-field">
                <label>Amount</label>
                <p>Rs. {selectedRequest?.amount || "500.00"}</p>
              </div>
              <div className="data-field">
                <label>Payment Status</label>
                <p>{selectedRequest?.PaymentReceipt?.status || "-"}</p>
              </div>
              <div className="data-field">
                <label>Transaction ID</label>
                <p>{selectedRequest?.PaymentReceipt?.gateway_payment_id || "-"}</p>
              </div>
              <div className="data-field">
                <label>Paid On</label>
                <p>{formatDateTime(selectedRequest?.PaymentReceipt?.paid_at) || "-"}</p>
              </div>

              {selectedRequest?.Member?.photo_url && (
                <Card className="mb-2">
                  <Card.Header className="text-center py-1">Photo</Card.Header>
                  <Card.Body className="p-2 text-center">
                    <img
                      src={getImageUrl(selectedRequest.Member.photo_url)}
                      alt="Profile"
                      style={{ width: "100%", borderRadius: 8 }}
                    />
                  </Card.Body>
                </Card>
              )}
             
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Form className="w-100">
            <Row>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Renewal Date (optional)</Form.Label>
                  <Form.Control
                    type="date"
                    value={renewalDate}
                    onChange={(e) => setRenewalDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Admin Remarks (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., Approved by admin"
                    value={adminRemarks}
                    onChange={(e) => setAdminRemarks(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
          <div className="w-100 d-flex justify-content-end">
          <Button
            variant="outline-secondary"
            onClick={() => setShowApproveModal(false)}
            disabled={approving}
            className="me-2 px-4"
          >
            <i className="bi bi-x-circle me-1"></i> Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleApprove}
            disabled={approving}
            className="px-4 fw-semibold"
          >
            {approving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" /> Approving...
              </>
            ) : (
              <>
                <i className="bi bi-check2-circle me-1"></i> Approve
              </>
            )}
          </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* ID Card Details Review/Edit Modal */}
      <Modal
        show={showIdCardDetailsModal}
        onHide={() => setShowIdCardDetailsModal(false)}
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Review ID Card Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="g-2">
              {[
                { key: "name", label: "Name", max: 32 },
                { key: "position", label: "Designation", max: 32 },
                { key: "phone", label: "Phone" },
                { key: "workingArea", label: "Working Area" },
                { key: "validDate", label: "Validity Date (DD/MM/YYYY)" },
                { key: "district", label: "District (prefixed with Distt-)" },
                { key: "statePin", label: "State + Pin" },
                { key: "vehicleNumber", label: "Vehicle Number" },
                { key: "idCardNumber", label: "ID Card Number" },
              ].map((field) => (
                <Col md={6} key={field.key}>
                  <Form.Group className="mb-1">
                    <Form.Label className="d-flex justify-content-between align-items-center">
                      <span>{field.label}{field.max ? ` (max ${field.max} chars)` : ""}</span>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() =>
                          setIdCardEditable((prev) => ({
                            ...prev,
                            [field.key]: !prev[field.key],
                          }))
                        }
                      >
                        <i className="bi bi-pencil-square me-1"></i>
                        {idCardEditable[field.key] ? "Lock" : "Edit"}
                      </Button>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      disabled={!idCardEditable[field.key]}
                      value={idCardDetails[field.key] || ""}
                      onChange={(e) =>
                        setIdCardDetails((prev) => ({
                          ...prev,
                          [field.key]:
                            field.key === "position"
                              ? e.target.value.toUpperCase()
                              : e.target.value,
                        }))
                      }
                    />
                    {field.max != null && (
                      <div
                        className={
                          (idCardDetails[field.key] || "").trim().length > field.max
                            ? "text-danger small mt-1"
                            : "text-success small mt-1"
                        }
                      >
                        {(idCardDetails[field.key] || "").trim().length}/{field.max}
                      </div>
                    )}
                  </Form.Group>
                </Col>
              ))}
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowIdCardDetailsModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowIdCardDetailsModal(false);
              generateDocument("id_card", {
                ...idCardDetails,
                name: (idCardDetails.name || "").trim(),
                position: (idCardDetails.position || "").trim(),
                vehicleNumber: idCardDetails.vehicleNumber || " ",
              });
            }}
            disabled={
              !(idCardDetails.name || "").trim() ||
              !(idCardDetails.position || "").trim() ||
              (idCardDetails.name || "").trim().length > 32 ||
              (idCardDetails.position || "").trim().length > 32
            }
          >
            Generate & Download
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSuccessModal}
        onHide={() => {
          setShowSuccessModal(false);
          if (hasDownloadedDoc) fetchPending();
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Renewal Successful</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2 fw-semibold">
            Renewal has been approved successfully.
          </p>
          <p className="text-muted mb-0">
            Download ID Card or Certificate before closing this dialog.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={() => {
              if (!renewedMember) return;
              const details = {
                name: renewedMember?.name || "",
                position: getDesignationLabel(renewedMember).toUpperCase(),
                phone: renewedMember?.mobile_no || "",
                workingArea: getWorkingArea(renewedMember),
                validDate: toDDMMYYYY(renewedValidDate || renewedMember?.expiry_date),
                district: `Distt-${renewedMember?.district || ""}`,
                statePin: `State-${renewedMember?.state || ""} Pin Code -${renewedMember?.pin || ""}`,
                vehicleNumber: renewedMember?.vehicle_no || " ",
                idCardNumber: renewedMember?.registration_id || "",
                photoUrl: getImageUrl(renewedMember?.photo_url),
              };
              setIdCardDetails(details);
              setIdCardEditable({
                name: false,
                position: false,
                phone: false,
                workingArea: false,
                validDate: false,
                district: false,
                statePin: false,
                vehicleNumber: false,
                idCardNumber: false,
                photoUrl: false,
              });
              setShowIdCardDetailsModal(true);
            }}
            disabled={isGeneratingDoc}
            className="fw-semibold"
          >
            <i className="bi bi-download me-1"></i> Download ID Card
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => generateDocument("certificate")}
            disabled={isGeneratingDoc}
            className="fw-semibold"
          >
            <i className="bi bi-download me-1"></i> Download Certificate
          </Button>
          <Button
            variant="success"
            onClick={() => {
              setShowSuccessModal(false);
              if (hasDownloadedDoc) fetchPending();
            }}
            className="px-4 fw-semibold"
          >
            <i className="bi bi-check2 me-1"></i> Done
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={isGeneratingDoc} centered backdrop="static" keyboard={false}>
        <Modal.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 mb-0">Generating image, please wait...</p>
        </Modal.Body>
      </Modal>

      <Modal
        show={showDocValidationModal}
        onHide={() => setShowDocValidationModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>ID Card Validation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Please reduce these fields to continue:</p>
          <ul>
            {docValidationMessages.map((m, idx) => (
              <li key={idx}>{m}</li>
            ))}
          </ul>
          {needsNameFix && (
            <Form.Group className="mb-2">
              <Form.Label className="d-flex justify-content-between">
                <span>Name (max 32 chars)</span>
                <span
                  className={
                    (customIdCardName || "").trim().length > 32
                      ? "text-danger fw-semibold"
                      : "text-success"
                  }
                >
                  {(customIdCardName || "").trim().length}/32
                </span>
              </Form.Label>
              <Form.Control
                type="text"
                maxLength={60}
                value={customIdCardName}
                onChange={(e) => setCustomIdCardName(e.target.value)}
              />
            </Form.Group>
          )}
          {needsDesignationFix && (
            <Form.Group>
              <Form.Label className="d-flex justify-content-between">
                <span>Designation (max 32 chars)</span>
                <span
                  className={
                    (customIdCardDesignation || "").trim().length > 32
                      ? "text-danger fw-semibold"
                      : "text-success"
                  }
                >
                  {(customIdCardDesignation || "").trim().length}/32
                </span>
              </Form.Label>
              <Form.Control
                type="text"
                maxLength={60}
                value={customIdCardDesignation}
                onChange={(e) => setCustomIdCardDesignation(e.target.value.toUpperCase())}
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDocValidationModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowDocValidationModal(false);
              generateDocument("id_card", {
                name: customIdCardName.trim(),
                position: customIdCardDesignation.trim(),
              });
            }}
            disabled={
              (needsNameFix &&
                (!customIdCardName.trim() || customIdCardName.trim().length > 32)) ||
              (needsDesignationFix &&
                (!customIdCardDesignation.trim() ||
                  customIdCardDesignation.trim().length > 32))
            }
          >
            Continue Download
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RenewalsPending;

